import type { CheckResult, CheckRunContext, ValidationCheck } from '../types/checks.js';
import type { DocumentProfile, ResolvedRule } from '../types/profile.js';
import type { ValidationContext } from '../types/checks.js';
import { withRequirement } from '../checks/support.js';
import { nowMs } from '../utils/clock.js';
import { resolveRule } from './profile.js';

/**
 * The rule engine.
 *
 * It owns three things and nothing else:
 *  1. Which checks exist (registry, injected).
 *  2. Which of them a document profile enables, and with which parameters.
 *  3. Turning a crashed check into an honest `not_checked` instead of a
 *     broken report or — worse — a silent pass.
 *
 * It contains zero document-specific branching: adding a document means adding
 * a JSON profile, never touching this file.
 */

export interface RuleEngineOptions {
  checks: readonly ValidationCheck[];
}

export interface ResolvedCheck {
  check: ValidationCheck;
  enabled: boolean;
  severity: 'info' | 'warning' | 'error';
  params: Record<string, unknown>;
  /** The full declarative rule, including official/policy/measurement blocks. */
  rule: ResolvedRule;
}

export interface RuleEngine {
  /** Every registered check, enabled for this profile or not. */
  resolve(profile: DocumentProfile): ResolvedCheck[];
  /** Runs one check, converting failures into `not_checked`. */
  run(resolved: ResolvedCheck, ctx: ValidationContext, profile: DocumentProfile): CheckResult;
  /** Runs every enabled check and returns all results. */
  runAll(
    profile: DocumentProfile,
    ctx: ValidationContext,
    options?: { skip?: (check: ValidationCheck) => boolean },
  ): CheckResult[];
}

export const createRuleEngine = (options: RuleEngineOptions): RuleEngine => {
  const registry = new Map<string, ValidationCheck>();
  for (const check of options.checks) {
    if (registry.has(check.id)) {
      throw new Error(`Проверка с id "${check.id}" зарегистрирована дважды.`);
    }
    registry.set(check.id, check);
  }

  const resolve = (profile: DocumentProfile): ResolvedCheck[] =>
    [...registry.values()].map((check) => {
      const rule = resolveRule(profile, check.id);
      return {
        check,
        enabled: rule.enabled,
        severity: rule.severity,
        params: rule.params,
        rule,
      };
    });

  const run = (
    resolved: ResolvedCheck,
    ctx: ValidationContext,
    profile: DocumentProfile,
  ): CheckResult => {
    const { check } = resolved;
    const runContext: CheckRunContext = {
      ...ctx,
      profile,
      rule: { ...resolved.rule, id: check.id, enabled: resolved.enabled },
    };

    const startedAt = nowMs();

    const finish = (result: CheckResult): CheckResult => {
      const finishedAt = nowMs();
      return {
        // Every result carries the official bounds, our measurement with its
        // tolerance, and an internal policy when one exists — uniformly, from
        // one place, so no check can forget the distinction.
        ...withRequirement({ ...result, id: check.id, title: check.title }, runContext.rule),
        durationMs: Math.round(finishedAt - startedAt),
      };
    };

    try {
      return finish(check.run(runContext));
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return {
        id: check.id,
        title: check.title,
        status: 'not_checked',
        severity: resolved.severity,
        message: `Проверка не выполнена из-за внутренней ошибки: ${message}`,
        fixability: 'none',
        details: { error: message },
        durationMs: 0,
      };
    }
  };

  const runAll = (
    profile: DocumentProfile,
    ctx: ValidationContext,
    options: { skip?: (check: ValidationCheck) => boolean } = {},
  ): CheckResult[] =>
    resolve(profile).map((resolved) => {
      if (options.skip?.(resolved.check)) {
        // A later stage (extended authenticity analysis) was not requested yet:
        // this is not a failure and not a capability problem, just "not yet".
        return {
          id: resolved.check.id,
          title: resolved.check.title,
          status: 'not_checked' as const,
          severity: resolved.severity,
          message: 'Проверка выполняется на этапе расширенного анализа изображения.',
          fixability: 'none' as const,
          durationMs: 0,
        };
      }
      if (!resolved.enabled) {
        return {
          id: resolved.check.id,
          title: resolved.check.title,
          status: 'not_checked' as const,
          severity: resolved.severity,
          message: 'Проверка отключена в правилах этого документа.',
          fixability: 'none' as const,
          durationMs: 0,
        };
      }
      return run(resolved, ctx, profile);
    });

  return { resolve, run, runAll };
};
